import type { UserNode } from '@revelio/data-access';

import type { CustomSkillTaxonomySelection } from '../../../filters/definitions/skillTaxonomy';
import { findFilterById } from '../../../filters/engine/filterRegistry';
import {
  type Filter,
  FilterId,
  FilterType,
  isMultiFilter,
  type SingleValueFilter,
  type TreeFilter,
} from '../../../filters/types/filters';
import { getSkillTierForLevelId } from '../../../filters/utils/role-skill-ent-kind';
import { buildTreeFilterNodes, wrapExprNode } from './node-builders';
import { TalentDiscoveryV3Tags } from './types';

/**
 * Map custom-skill tree depth (`skill_0` … `skill_4`) to TD `UserCategoryKind`.
 *
 * Talent Discovery only accepts `SKILL_LVL_1`–`SKILL_LVL_5`.
 */
const skillLevelIdToTagMap: Record<string, TalentDiscoveryV3Tags> = {
  skill_0: TalentDiscoveryV3Tags.SkillLvl_1,
  skill_1: TalentDiscoveryV3Tags.SkillLvl_2,
  skill_2: TalentDiscoveryV3Tags.SkillLvl_3,
  skill_3: TalentDiscoveryV3Tags.SkillLvl_4,
  skill_4: TalentDiscoveryV3Tags.SkillLvl_5,
};

/**
 * Build custom skill filter node from filter state.
 * This is the single-filter (non-MULTI) variant used on non-TD pages.
 */
export const buildCustomSkillFilter = (filters: Filter[]): UserNode | null => {
  const customSkillFilter = findFilterById(filters, FilterId.CUSTOM_SKILL);
  if (!customSkillFilter || customSkillFilter.type !== FilterType.TREE) {
    return null;
  }

  const exprNode = buildTreeFilterNodes(
    customSkillFilter,
    skillLevelIdToTagMap,
    undefined,
    {
      taxonomyId: getSkillTaxonomyId(filters),
      resolveLevel: getSkillTierForLevelId,
    }
  );
  if (!exprNode) return null;

  return wrapExprNode(exprNode, customSkillFilter.isNot);
};

/**
 * Extract skill taxonomy ID from filters
 */
export const getSkillTaxonomyId = (filters: Filter[]): number => {
  const skillTaxonomyFilter = findFilterById(filters, FilterId.SKILL_TAXONOMY);
  if (!skillTaxonomyFilter || skillTaxonomyFilter.type !== FilterType.VALUE) {
    return 0;
  }

  const value = (
    skillTaxonomyFilter as SingleValueFilter<CustomSkillTaxonomySelection>
  ).value;
  if (value && typeof value.taxonomyId === 'number') {
    return value.taxonomyId;
  }
  return 0;
};

/**
 * Build multi custom skill filter node from filter state.
 * Each instance becomes an OR group, and instances are ANDed together.
 * Returns an array of nodes when there are multiple instances (to avoid nested AND nodes).
 */
export const buildMultiCustomSkillFilter = (
  filters: Filter[]
): UserNode | UserNode[] | null => {
  const multiCustomSkill = findFilterById(filters, FilterId.MULTI_CUSTOM_SKILL);

  if (!multiCustomSkill || !isMultiFilter(multiCustomSkill)) {
    return null;
  }

  if (multiCustomSkill.instances.length === 0) {
    return null;
  }

  const taxonomyId = getSkillTaxonomyId(filters);

  const instanceNodes = multiCustomSkill.instances
    .filter((inst): inst is TreeFilter => inst?.type === FilterType.TREE)
    .map((instance) => {
      const exprNode = buildTreeFilterNodes(
        instance,
        skillLevelIdToTagMap,
        undefined,
        { taxonomyId, resolveLevel: getSkillTierForLevelId }
      );
      if (!exprNode) return null;

      return wrapExprNode(exprNode, instance.isNot);
    })
    .filter((node): node is UserNode => node !== null);

  if (instanceNodes.length === 0) return null;
  if (instanceNodes.length === 1) {
    const [singleNode] = instanceNodes;
    return singleNode ?? null;
  }

  return instanceNodes;
};
